<template>
    <DsDropdown ref="dropdown" @close="resetMenu">
        <template #default>
            <DsIconButton
                name="filter"
                data-qa="filter-button"
                :active="menuOpen"
                @click="toggleOpen"
            />
        </template>

        <template #menu>
            <CustomMultiselectMenu
                ref="menu"
                categorized
                multiple
                class="automation-filter-menu"
                data-qa="filter-menu"
                :options="options"
                :value="[]"
                @selectCategory="onSelectCategory"
                @input="emitSelection"
            >
                <template #option="{ option, isCategory, isBreadcrumb }">
                    <div
                        v-if="isCategory && isBreadcrumb"
                        class="menu-item breadcrumb"
                        data-qa="breadcrumb"
                        @click.stop="backToCategory"
                    >
                        <span class="option-text">{{ option.label }}</span>
                    </div>

                    <div
                        v-else-if="isCategory && !isBreadcrumb"
                        class="filter-row"
                        :class="{ 'has-children': option.hasChildrenOptions,
                                  'with-help': !!option.helpText,
                                  'with-separator': !!option.separator
                        }"
                        :data-qa="'cat-' + option.value"
                        @click.capture="maybeHandleCategory(option, $event)"
                    >
                        <template v-if="isPathIcon(option.icon)">
                            <img :src="option.icon" alt="" class="filter-imgicon" />
                        </template>
                        <template v-else-if="option.icon">
                            <DsIcon :name="option.icon" class="filter-icon" />
                        </template>
                        <span class="element-section-title">{{ option.label }}</span>
                        <span v-if="option.helpText" class="category-help">{{ option.helpText }}</span>

                        <span v-if="option.hasChildrenOptions" class="chevron-wrapper">
                            <DsIcon name="chevron-right" class="chevron-icon" />
                        </span>
                    </div>

                    <div
                        v-else-if="option && !option.isLabel"
                        :class="['filter-row','filter-row--child', { 'no-icon': !option.icon }]"
                        :data-qa="'filter-opt-' + (option.value || '')"
                        @click.capture="onOptionClick(option)"
                    >
                        <span class="filter-left">
                            <template v-if="isPathIcon(option.icon)">
                                <img :src="option.icon" alt="" class="filter-imgicon" />
                            </template>
                            <template v-else-if="option.icon">
                                <DsIcon :name="option.icon" class="filter-icon" />
                            </template>
                            <span class="element-section-title">{{ option.label || '' }}</span>
                        </span>
                    </div>
                </template>
            </CustomMultiselectMenu>
        </template>
    </DsDropdown>
</template>

<script>

import CustomMultiselectMenu from '@/components/shared/CustomMultiselectMenu.vue';
import { getIconPathByCellType } from '../../utils/cell/automation-display-utils';
import {
    GOALS,
    INTEGRATION_TRIGGER_TYPE,
    DECISION_TYPE,
    NOTES_TYPE,
    CELL_TOOLTIP,
    SEQUENCE_START,
    EDGE_TYPE,
} from '../../utils/editor.constants';

export default {
    name: 'FilterActionButton',
    components: {
        CustomMultiselectMenu,
    },
    props: {
        value: { type: Array, default: () => [] },
        cells: { type: Array, default: () => [] },
        publishedCells: { type: Array, default: () => [] },
    },
    emits: ['input', 'chip:add'],
    data() {
        return {
            internal: [...this.value],
            menuOpen: false,
            ignoreNextDsUpdate: false,
        };
    },
    computed: {
        options() {
            const whenOpts = this.usedTypeOptions('when', 'green');
            const thenOpts = this.usedTypeOptions('then', 'blue');

            const whenCount = whenOpts.length;
            const thenCount = thenOpts.length;
            const decisionCount = this.cells.filter((c) => c?.type === DECISION_TYPE).length;
            const notesCount = this.cells.filter((c) => c?.type === NOTES_TYPE).length;
            const statusCount = this.statusOptions.length;

            return [
                {
                    category: {
                        value: 'when',
                        label: this.$t('funnel.findElement.when'),
                        icon: 'zap',
                        helpText: this.$tc('funnel.findElement.count', whenCount, { count: whenCount }),
                    },
                    options: whenOpts,
                },
                {
                    category: {
                        value: 'then',
                        label: this.$t('funnel.findElement.then'),
                        icon: 'layers',
                        helpText: this.$tc('funnel.findElement.count', thenCount, { count: thenCount }),
                    },
                    options: thenOpts,
                },
                {
                    category: {
                        value: 'decision',
                        label: this.$t('funnel.findElement.decision'),
                        icon: '/resources/funnel/images/svg/cell-icon/jump-to-element/decision.svg',
                        helpText: this.$tc('funnel.findElement.count', decisionCount, { count: decisionCount }),
                    },
                },
                {
                    category: {
                        value: 'notes',
                        label: this.$t('funnel.findElement.notes'),
                        icon: 'edit',
                        helpText: this.$tc('funnel.findElement.count', notesCount, { count: notesCount }),
                    },
                },
                {
                    category: {
                        value: 'status',
                        label: this.$t('funnel.findElement.status'),
                        icon: 'info-circle',
                        helpText: this.$tc('funnel.findElement.selections.count', statusCount, { count: statusCount }),
                    },
                    separator: true,
                    options: this.statusOptions,
                },
            ];
        },
        statusOptions() {
            const entries = [
                { key: 'setupRequired', i18n: CELL_TOOLTIP.elementSetupRequired },
                { key: 'ready', i18n: CELL_TOOLTIP.elementReady },
                { key: 'published', i18n: CELL_TOOLTIP.elementPublished },
                { key: 'unpublished', i18n: CELL_TOOLTIP.elementPublishedWithChanges },
                { key: 'error', i18n: CELL_TOOLTIP.elementBroken },
            ];

            return entries.map(({ key, i18n }) => ({
                value: `status:${key}`,
                label: this.$t(i18n),
            }));
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(v) {
                this.internal = Array.isArray(v) ? v.slice() : [];
            },
        },
    },
    methods: {
        getMenuScrollerEl() {
            const root = this.$refs.menu && this.$refs.menu.$el;

            if (!root) return null;

            const scroller = root.closest('.menu');

            if (scroller) return scroller;

            let el = root.parentElement;

            while (el) {
                const { overflowY } = getComputedStyle(el);

                if (/(auto|scroll)/.test(overflowY)) return el;
                el = el.parentElement;
            }

            return null;
        },

        scrollMenuTopNow() {
            const el = this.getMenuScrollerEl();

            if (el) {
                el.style.overflowAnchor = 'none';
                el.scrollTop = 0;
            }
        },

        scrollMenuTopSoon() {
            this.$nextTick(() => {
                setTimeout(() => this.scrollMenuTopNow(), 0);
            });
        },

        maybeHandleCategory(cat, evt) {
            const leaf = new Set(['decision', 'notes']);
            const parents = new Set(['status', 'when', 'then']);

            if (leaf.has(cat?.value)) {
                evt.stopPropagation();
                this.onSelectCategory(cat);

                return;
            }

            if (parents.has(cat?.value) || cat?.hasChildrenOptions) {
                this.scrollMenuTopSoon();
            }
        },

        isPathIcon(i) {
            return typeof i === 'string' && (i.includes('/') || i.endsWith('.svg'));
        },

        toggleOpen() {
            this.menuOpen = !this.menuOpen;

            if (this.menuOpen) {
                this.$refs.menu?.backToCategory?.();

                this.scrollMenuTopSoon();
            }
        },

        backToCategory() {
            const m = this.$refs && this.$refs.menu;

            if (m && typeof m.backToCategory === 'function') {
                m.backToCategory();
                this.scrollMenuTopSoon();
            }
        },

        resetMenu() {
            this.menuOpen = false;
            this.$refs.menu?.backToCategory?.();
        },

        closeDropdown() {
            this.menuOpen = false;
            this.$refs.dropdown?.close?.();
            this.$refs.menu?.backToCategory?.();
        },

        onOptionClick(option) {
            const key = option.value;
            const next = this.mergeKey(this.internal, key);

            this.pushModel(next);

            this.$emit('chip:add', { key, label: option.label, icon: option.icon, source: 'option' });

            this.ignoreNextDsUpdate = true;
            this.closeDropdown();
        },

        onSelectCategory(cat) {
            const parents = new Set(['status', 'when', 'then']);

            if (parents.has(cat?.value)) {
                this.scrollMenuTopSoon();

                return;
            }

            const selectable = new Set(['decision', 'notes']);

            if (!cat?.value || !selectable.has(cat.value)) {
                this.$refs.menu?.backToCategory?.();

                return;
            }

            const key = `section:${cat.value}`;
            const label = cat.value === 'decision'
                ? this.$t('funnel.findElement.decision')
                : this.$t('funnel.findElement.notes');

            const next = this.mergeKey(this.internal, key);

            this.pushModel(next);

            this.$emit('chip:add', { key, label, icon: cat.value === 'decision' ? 'square' : 'edit', source: 'category' });

            this.ignoreNextDsUpdate = true;
            this.closeDropdown();
        },

        emitSelection(val) {
            if (this.ignoreNextDsUpdate) {
                this.ignoreNextDsUpdate = false;

                return;
            }
            const next = this.normalize(val);

            this.pushModel(next);
        },

        mergeKey(list, key) {
            return Array.from(new Set([...(list || []), key]));
        },

        normalize(v) {
            if (Array.isArray(v)) return v;
            if (v && Array.isArray(v.detail)) return v.detail;
            if (v && v.target && Array.isArray(v.target.value)) return v.target.value;

            return this.internal.slice();
        },

        pushModel(next) {
            const plain = next.slice();

            this.internal = plain;
            this.$emit('input', plain);
        },

        usedTypeOptions(bucket, color) {
            const isWhenType = (t) => [...Object.values(GOALS), INTEGRATION_TRIGGER_TYPE].includes(t);
            const raw = this.cells
                .filter((c) => {
                    if (!c?.type) return false;
                    if ([NOTES_TYPE, DECISION_TYPE, SEQUENCE_START, EDGE_TYPE].includes(c.type)) return false;
                    const when = isWhenType(c.type);

                    return bucket === 'when' ? when : !when;
                })
                .map((c) => ({
                    type: c.type,
                    label: this.typeLabel(c.type),
                    icon: getIconPathByCellType(c.type, color) || null,
                }));

            const byType = new Map();

            raw.forEach((r) => { if (!byType.has(r.type)) byType.set(r.type, r); });

            return [...byType.values()]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(({ type, label, icon }) => ({ value: `${bucket}:${type}`, label, icon }));
        },

        typeLabel(type) {
            const isWhen = this.$te(`funnel.sidebar.when.cell.${type}.label`);
            const isThen = this.$te(`funnel.sidebar.then.cell.${type}.label`);
            const isOther = this.$te(`funnel.type.${type}.label`);
            const key = isWhen ? `funnel.sidebar.when.cell.${type}.label`
                : isThen ? `funnel.sidebar.then.cell.${type}.label`
                    : isOther ? `funnel.type.${type}.label`
                        : type;

            return this.$t(key);
        },
    },
};
</script>

<style lang="scss" scoped>
.filter-row {
    display: grid;
    grid-template-columns: 22px 1fr max-content;
    column-gap: $spacing-150;
    align-items: center;
    padding: $spacing-200 $spacing-200;
    width: 100%;
}

.filter-row.has-children {
    padding-right: $spacing-200;
}

.filter-imgicon,
.filter-icon {
    grid-column: 1;
    grid-row: 1;
    width: 24px;
    height: 24px;
    align-self: center;
}

.filter-row.with-help .filter-imgicon,
.filter-row.with-help .filter-icon {
    grid-row: 1 / span 2;
}

.filter-row--child .filter-imgicon,
.filter-row--child .filter-icon {
    width: 24px;
    height: 24px;
}

.filter-row--child.no-icon {
    grid-template-columns: 1fr;
    padding: $spacing-100 $spacing-200;
}

.filter-row--child.no-icon .filter-imgicon,
.filter-row--child.no-icon .filter-icon {
    display: none;
}

.filter-row--child.no-icon .element-section-title {
    grid-column: 1;
}

.chevron-wrapper {
    grid-column: 3;
    grid-row: 1 / span 2;
    justify-self: end;
    align-self: center;
    display: inline-flex;
    margin-right: 0;
}

.filter-row--child {
    padding: $spacing-150 $spacing-200;
    min-height: auto;
}

.filter-left {
    display: contents;
}

.element-section-title {
    grid-column: 2;
    font-size: $font-size-md;
    line-height: $spacing-300;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.category-help {
    grid-column: 2;
    grid-row: 2;
    margin-top: $spacing-025;
    font-size: $font-size-sm;
    line-height: $spacing-250;
    color: $color-ink-800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chevron-icon {
    width: $spacing-150;
    height: $spacing-150;
    color: $color-ink-800;
}

.chevron-icon :deep(svg) {
    transform: scaleX(0.6);
    transform-origin: center;
}

.automation-filter-menu :deep(li.separator) {
    border-bottom: 1px solid $color-ink-100;
    margin: $spacing-050 0 0 0;
}

.automation-filter-menu :deep(.menu-item.breadcrumb) {
    display: flex;
    align-items: center;
    gap: $spacing-150;
    padding: $spacing-200 $spacing-200;
    border-bottom: 1px solid $color-ink-100;
    cursor: pointer;
}

.automation-filter-menu :deep(.menu-item.breadcrumb .category-icon) {
    display: none;
}

.automation-filter-menu :deep(.menu-item.breadcrumb)::before {
    content: '';
    display: inline-block;
    width: 24px; height: 24px; background: currentColor;
    -webkit-mask: url('/resources/svg/arrow-left.svg') no-repeat center / contain;
    mask: url('/resources/svg/arrow-left.svg') no-repeat center / contain;
}

.automation-filter-menu :deep(.menu-item.breadcrumb .option-text) {
    font-weight: 400;
    font-size: $font-size-md;
    line-height: $spacing-300;
    margin-top: 0;
}

[dir="rtl"] .automation-filter-menu :deep(.menu-item.breadcrumb)::before {
    transform: scaleX(-1);
}

.automation-filter-menu:deep(.multiselect-menu ul.dropdown-list > li),
.automation-filter-menu:deep(.multiselect-menu ul > li) {
    padding: 0;
}

.filter-row.with-separator {
    border-top: 1px solid $color-ink-100;
    margin-top: $spacing-050;
}

[data-qa="cat-when"]  > .filter-icon.icon,
[data-qa="cat-then"]  > .filter-icon.icon,
[data-qa="cat-notes"] > .filter-icon.icon,
[data-qa="cat-status"]> .filter-icon.icon {
  color: $color-ink-900;
  opacity: .70;
  stroke-width: 1.25;
}

</style>
